/**
 * Fixed-light route boundary.
 *
 * Authentication flows stay light regardless of preference. Guest commerce
 * surfaces honor the stored theme so the menu, booking, payment and status
 * screens stay consistent during one visit.
 *
 * Everything else public — marketing, legal, docs — is THEME-AWARE: it honors
 * the stored `menuraq-theme` preference (or OS fallback) exactly like the
 * authenticated product, and PublicHeader offers a dark/light toggle there.
 *
 * Chow (`/c/*`, `/chow/*`) is deliberately EXCLUDED from this list too: it is a
 * signed-in consumer product with its own per-user theme preference.
 *
 * This list is consumed in two places that must stay in agreement:
 *   1. `THEME_NO_FLASH_SCRIPT` (serialized below via JSON) so the pre-paint
 *      script resolves these routes to light without a flash of dark.
 *   2. `ThemeProvider`, which re-applies the boundary on every client-side
 *      navigation between fixed-light and theme-aware surfaces.
 */

/** Top-level paths whose whole subtree is fixed-light (no dynamic segments). */
export const FIXED_LIGHT_EXACT_PATHS: readonly string[] = [
  '/gift-cards',
  '/handoff',
  '/previews',
  '/templates',
];

/** Path prefixes whose whole subtree is fixed-light. */
export const FIXED_LIGHT_PREFIXES: readonly string[] = [
  '/auth',
  '/business',
  '/display',
  '/invoice',
  '/kiosk',
  '/loyalty',
  '/preview',
  '/q',
];

function normalizePath(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.slice(0, -1);
  }
  return pathname;
}

/**
 * Whether a route renders the fixed-light experience regardless of the
 * user's theme preference. Trailing slashes are normalized; query strings
 * are expected to be absent (pass `location.pathname`, never `location.href`).
 */
export function isFixedLightRoute(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  const path = normalizePath(pathname);
  if ((FIXED_LIGHT_EXACT_PATHS as readonly string[]).includes(path)) return true;
  return FIXED_LIGHT_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
}
